import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import loginImage from "../../../assets/reading.svg";
import ForgotPasswordImage from "../../../assets/forgot.svg";
import ResetPasswordImage from "../../../assets/reset.svg"; // New asset for Reset Password
import SignInForm from "./SignInForm";
import AuthSideItem from "./AuthSideItem";
import SignUpForm from "./SignUpForm";
import SignUpImage from "../../../assets/celebration.svg";
import ForgotPasswordForm from "./ForgotPasswordForm";
import ResetPasswordForm from "./ResetPasswordForm"; // Import Reset Password form
import useResponsiveMode from "../../../hooks/useResponsiveMode";

const AuthenticationCard = ({ResetPassword}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPhone, isTablet } = useResponsiveMode();

  // Determine the current route
  const isSignUp = location.pathname.includes("sign-up");
  const searchParams = new URLSearchParams(location.search);
  const isResetPassword = ResetPassword || searchParams.get("step") === "reset";
  const isForgotPassword = location.pathname.includes("forgot-password") && !isResetPassword;

  const formPanel = isSignUp ? (
    <div className="w-full h-full flex items-center transition-opacity duration-500 opacity-100">
      <SignUpForm />
    </div>
  ) : isForgotPassword ? (
    <div className="w-full h-full flex items-center transition-opacity duration-500 opacity-100">
      <div className="w-full text-xl">
        <ForgotPasswordForm />
      </div>
    </div>
  ) : isResetPassword ? (
    <div className="w-full h-full flex items-center transition-opacity duration-500 opacity-100">
      <div className="w-full text-xl">
        <ResetPasswordForm />
      </div>
    </div>
  ) : (
    <div className="w-full h-full flex items-center transition-opacity duration-500 opacity-100">
      <SignInForm />
    </div>
  );

  const supportPanel = isSignUp ? (
    <div className="w-full h-full transition-opacity duration-500 opacity-100">
      <AuthSideItem
        image={SignUpImage}
        altText="Sign Up"
        title="Join Us Today! 🚀"
        subtitle={
          <div className="space-y-2">
            <p>Take the first step toward achieving your financial goals with WiseApp.</p>
            <p>
              Already have an account?{" "}
              <strong
                className="cursor-pointer font-bold hover:underline animate-pulse"
                onClick={() => navigate("/sign-in")}
              >
                Log in to continue.
              </strong>
            </p>
          </div>
        }
      />
    </div>
  ) : isForgotPassword ? (
    <div className="w-full h-full transition-opacity duration-500 opacity-100">
      <AuthSideItem
        image={ForgotPasswordImage}
        altText="Forgot Password"
        title="Forgot Your Password? 🔒"
        subtitle={
          <div className="space-y-2">
            <p>Don’t worry! We’ll help you securely reset it and get back on track.</p>
            <p>
              Remembered your password?
              <strong
                className="cursor-pointer font-bold hover:underline animate-pulse"
                onClick={() => navigate("/sign-in")}
              >
                {" "}
                Back to login.
              </strong>
            </p>
          </div>
        }
        buttonText="Back to Login"
        buttonAction={() => navigate("/sign-in")}
      />
    </div>
  ) : isResetPassword ? (
    <div className="w-full h-full transition-opacity duration-500 opacity-100">
      <AuthSideItem
        image={ResetPasswordImage}
        altText="Reset Password"
        title="Reset Your Password 🔐"
        subtitle={
          <div className="space-y-2">
            <p>Enter your new password below to access your account securely.</p>
            <p>
              Need help?{" "}
              <strong
                className="cursor-pointer font-bold hover:underline animate-pulse"
                onClick={() => window.location.href = "mailto:wiseapp60@gmail.com"}
              >
                Contact support.
              </strong>
            </p>
          </div>
        }
      />
    </div>
  ) : (
    <div className="w-full h-full transition-opacity duration-500 opacity-100">
      <AuthSideItem
        image={loginImage}
        altText="Login"
        title="Welcome Back! 👋"
        subtitle={
          <div className="space-y-2">
            <p>Log in to continue your journey toward financial freedom.</p>
            <p>
              New here?{" "}
              <strong
                className="cursor-pointer font-bold hover:underline animate-pulse"
                onClick={() => navigate("/sign-up")}
              >
                Sign up now!
              </strong>
            </p>
          </div>
        }
        buttonText="Sign up"
        buttonAction={() => navigate("/sign-up")}
      />
    </div>
  );

  if (isPhone) {
    return (
      <div className="w-full max-w-xl px-4 py-6">
        <div className="rounded-[28px] border border-white/20 bg-white/95 p-5 shadow-modal backdrop-blur-xl">
          <div className="mb-5 rounded-2xl bg-gradient-to-br from-primary via-primary-700 to-primary-900 p-5 text-white shadow-card">
            <p className="type-overline text-white/65">WiseApp</p>
            <h1 className="mt-2 text-2xl font-bold leading-tight">
              {isSignUp
                ? "Create your account and start planning smarter."
                : isForgotPassword
                ? "Recover access without leaving your phone flow."
                : isResetPassword
                ? "Choose a new password and get back in."
                : "Welcome back to your money, home, and goals hub."}
            </h1>
            <p className="mt-2 text-sm text-white/75">
              Your all-in-one hub for planning and achieving goals.
            </p>
          </div>
          {formPanel}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`grid w-full rounded-3xl bg-white/95 p-4 shadow-modal backdrop-blur-xl ${
        isTablet
          ? "max-w-5xl grid-cols-[0.92fr_1.08fr] gap-4"
          : "min-h-[40rem] max-w-6xl grid-cols-2 gap-4"
      }`}
    >
      <div className={`rounded-3xl overflow-hidden ${isTablet ? "hidden md:block" : "block"}`}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isSignUp ? "side-signup" : "side-signin"}
            initial={{ x: isSignUp ? 60 : -60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: isSignUp ? -60 : 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="h-full w-full"
          >
            {supportPanel}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="h-full rounded-3xl flex flex-col justify-center">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isSignUp ? "form-signup" : "form-signin"}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22 }}
            className="w-full"
          >
            {formPanel}
          </motion.div>
        </AnimatePresence>

      </div>
    </div>
  );
};

export default AuthenticationCard;
